import { Link } from 'react-router-dom';

/**
 * Brand mark — an editorial diamond facet emblem paired with
 * the "Jewelry Refined" wordmark. Designed for the luxury magazine aesthetic.
 *
 * Variants:
 *   - "dark"  → for use on light surfaces (default Navbar)
 *   - "light" → for use on dark surfaces (Footer)
 *   - "compact" → mark + smaller wordmark (mobile sheet)
 */
export const Logo = ({ variant = 'dark', size = 'md', linkTo = '/', className = '' }) => {
  const isLight = variant === 'light';

  const ink = isLight ? '#FAF9F6' : '#111111';
  const gold = '#C69C6D';
  const sub = isLight ? 'rgba(250,249,246,0.65)' : 'rgba(17,17,17,0.55)';

  const wordmarkSize = {
    sm: 'text-xl',
    md: 'text-2xl lg:text-[28px]',
    lg: 'text-3xl lg:text-4xl',
  }[size];

  const markSize = {
    sm: 28,
    md: 34,
    lg: 42,
  }[size];

  const content = (
    <span className={`group inline-flex items-center gap-3 ${className}`} data-testid="brand-logo">
      {/* SVG facet mark */}
      <span
        className="relative flex items-center justify-center transition-transform duration-500 group-hover:rotate-[8deg]"
        aria-hidden="true"
      >
        <svg
          width={markSize}
          height={markSize}
          viewBox="0 0 48 48"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          {/* Outer diamond silhouette */}
          <path
            d="M24 3 L44 18 L24 45 L4 18 Z"
            stroke={ink}
            strokeWidth="1.4"
            strokeLinejoin="round"
          />
          {/* Crown facets */}
          <path d="M4 18 L24 18 L44 18" stroke={ink} strokeWidth="1" />
          <path d="M14 9 L24 18 L34 9" stroke={ink} strokeWidth="1" />
          {/* Pavilion facets */}
          <path d="M4 18 L24 32 L44 18" stroke={ink} strokeWidth="1" />
          <path d="M24 18 L24 32" stroke={ink} strokeWidth="1" />
          {/* Gold accent — culet sparkle */}
          <circle cx="24" cy="32" r="1.6" fill={gold} />
          {/* Gold accent — top facet */}
          <path
            d="M14 9 L24 3 L34 9"
            stroke={gold}
            strokeWidth="1.4"
            strokeLinejoin="round"
            fill="none"
          />
        </svg>
      </span>

      {/* Wordmark */}
      <span className="flex flex-col leading-none">
        <span
          className={`font-heading ${wordmarkSize} tracking-tight`}
          style={{ color: ink }}
        >
          Jewelry <span className="italic font-light">Refined</span>
        </span>
        <span
          className="hidden sm:block text-[9px] tracking-[0.3em] uppercase mt-1.5 font-sans"
          style={{ color: sub }}
        >
          Pre-Owned · Luxury · Authenticated
        </span>
      </span>
    </span>
  );

  if (!linkTo) return content;

  return (
    <Link to={linkTo} className="inline-block" data-testid="logo-link">
      {content}
    </Link>
  );
};

export default Logo;
